import { ArrowUpRightOnBox, SquareTwoStack } from "@medusajs/icons"
import { Badge, Button, Text, Textarea, toast, usePrompt } from "@medusajs/ui"
import { useEffect, useState, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { copyText } from "../lib/clipboard"
import { t } from "../lib/i18n"
import { errorMessage } from "../lib/medusa"
import { MESSAGE_BY_KEY, toTemplate, useMessageTemplates } from "../lib/messages"

type Vars = Record<string, string | number | null | undefined>

/**
 * A ready message for a customer (lib/messages.ts): the wording in force filled in for this order,
 * editable before copying, with the Instagram DM one tap away. An edit can be saved as the wording
 * for everyone from now on: this order's own values go back to placeholders first, and the box says
 * exactly what will be saved.
 */
export function MessageBox({ mkey, vars, title, label, hint, handle, extra, framed = true }: { mkey: string; vars: Vars; title?: ReactNode; label?: string; hint?: string; handle?: string | null; extra?: ReactNode; framed?: boolean }) {
  const { message, loaded, save } = useMessageTemplates()
  const prompt = usePrompt()
  const filled = message(mkey, vars)
  const [text, setText] = useState(filled)
  // A new wording, or the order moving on, replaces what is in the box (an unsaved edit included).
  useEffect(() => setText(filled), [filled])
  const edited = loaded && text.trim() !== filled.trim()
  const keep = async () => {
    const template = toTemplate(text.trim(), vars)
    const ok = await prompt({
      title: t("Use this wording from now on?"),
      description: t("For every order from now on, «{message}» will read: {template}", { message: t(MESSAGE_BY_KEY.get(mkey)?.label ?? mkey), template }),
      confirmText: t("Save the wording"),
      cancelText: t("Cancel"),
    })
    if (!ok) return
    save.mutate({ key: mkey, text: template }, { onSuccess: () => toast.success(t("Saved: this wording is used from now on.")), onError: (error) => toast.error(errorMessage(error)) })
  }
  const body = (
    <div className="flex flex-col gap-y-2">
      {(label || hint) && (
        <div className="flex flex-col items-start gap-y-1">
          {label && <Badge size="2xsmall" color="grey" className="whitespace-nowrap">{label}</Badge>}
          {hint && <Text size="xsmall" className="text-ui-fg-muted">{hint}</Text>}
        </div>
      )}
      <Textarea rows={4} value={text} onChange={(event) => setText(event.target.value)} aria-label={typeof title === "string" ? title : t("Message for the customer")} />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="small" onClick={() => void copyText(text)} disabled={!text.trim()}>
          <SquareTwoStack /> {t("Copy")}
        </Button>
        {handle && (
          <a href={`https://ig.me/m/${encodeURIComponent(handle)}`} target="_blank" rel="noreferrer" className="txt-compact-small-plus text-ui-fg-interactive inline-flex items-center gap-x-1">
            {t("Open the DM")} <ArrowUpRightOnBox />
          </a>
        )}
        {edited && (
          <Button size="small" variant="secondary" isLoading={save.isPending} onClick={() => void keep()}>
            {t("Save as the wording")}
          </Button>
        )}
        {extra}
        <Link to={`/messages#${encodeURIComponent(mkey)}`} className="txt-compact-xsmall text-ui-fg-muted ml-auto">
          {t("All messages")}
        </Link>
      </div>
    </div>
  )
  if (!framed) return body
  return (
    <section className="bg-ui-bg-base shadow-elevation-card-rest flex flex-col gap-y-3 rounded-lg p-4">
      <h2 className="h2-core">{title ?? t("Message for the customer")}</h2>
      {body}
    </section>
  )
}
